<script setup>
import { ref, computed, watch, onMounted, inject, useAttrs } from 'vue';
import { debounce, uniqBy } from 'lodash';
import usuarios from '@/api/usuarios';

const props = defineProps({
  modelValue: { type: [Number, String, Array], default: undefined },
  placeholder: {
    type: String,
    default: undefined,
  },
  multiple: { type: Boolean, default: false },
  fullNameFormat: { type: Boolean, default: true },
  icon: { type: Boolean, default: false },
  // Muestra los textos a la derecha del option "click para seleccionar"
  showLabels: { type: Boolean, default: false },
  // profesor, alumno, padre
  tipo: { type: String, default: null },
  activos: { type: Boolean, default: null },
  precargar: { type: Boolean, default: false },
  // Debe ser un array de objetos usuario
  usuariosFijos: { type: Array, default: null },
});

const emit = defineEmits(['update:modelValue', 'usuarios-preparados']);

const attrs = useAttrs();
const $I18n = inject('I18n');

const usuariosLista = ref(prepararUsuariosIniciales());
const localValue = ref(null);
const loading = ref(false);
const omitirSiguiente = ref(false);

const placeholderFinal = computed(() => {
  return props.placeholder !== undefined ? props.placeholder : $I18n.t('introduzca_usuario');
});

const usuarioActual = computed(() => {
  return localValue.value;
});

// Debounce function for ajax search
const ajaxSearch = debounce((texto) => {
  const params = { q: texto };
  if (props.tipo) {
    params[props.tipo] = true;
  }
  if (props.activos !== null) {
    params.activo = props.activos;
  }
  usuarios.list({ params }).then((result) => {
    usuariosLista.value = prepararUsuarios(result.data);
    loading.value = false;
  }).catch((_result) => {
    loading.value = false;
  });
}, 350);

function prepararUsuarios(users) {
  let usuariosFiltrados = users.filter((user) => user);
  usuariosFiltrados = uniqBy(usuariosFiltrados, 'id');
  usuariosFiltrados.forEach((usuario) => {
    usuario.fullname = usuarios.fullname(usuario, props.fullNameFormat);
    if (props.icon) {
      usuario.icon = usuarioIcono(usuario);
    }
  });
  emit('usuarios-preparados', usuariosFiltrados);
  return usuariosFiltrados;
}

function prepararUsuariosIniciales() {
  if (!props.usuariosFijos) {
    return [];
  }

  let resultado;
  const nombreGrupo = attrs['group-values'];
  // Si está agrupado, cogemos los valores del grupo
  if (nombreGrupo) {
    resultado = props.usuariosFijos.map((grupo) => {
      grupo[nombreGrupo] = prepararUsuarios(grupo[nombreGrupo]);
      return grupo;
    });
  } else {
    resultado = prepararUsuarios(props.usuariosFijos);
  }
  return resultado;
}

function usuarioIcono(usuario) {
  let icono = 'fa fa-user fa-blank';
  let title = '';
  if (usuario.caracteristicas.alumno) {
    icono = 'fa fa-user text-alumno';
    title = $I18n.t('Alumno');
  } else if (usuario.caracteristicas.profesor) {
    icono = 'fa fa-user-graduate text-profesor';
    title = $I18n.t('Profesor');
  } else if (usuario.caracteristicas.padre) {
    icono = 'fa fa-user-tie text-padre';
    title = $I18n.t('Padre');
  }
  return { icono, title };
}

function onSearch(texto) {
  // Si son usuarios fijos, no buscamos con ajax
  if (props.usuariosFijos) {
    return;
  }
  if (texto.length) {
    // Si está precargado ya filtra el componente, no llamamos más ajax
    if (!props.precargar) {
      loading.value = true;
      ajaxSearch(texto);
    }
  }
}

// Traído de https://github.com/shentao/vue-multiselect/blob/ee7bf2170e591fe55caa31d1a9aeb7d90993e7e5/src/multiselectMixin.js#L28
// para pintar bien si es una opción o un tag o un optgroup
function getOptionLabel(option) {
  if (!option) return '';
  if (option.isTag) return option.label;
  if (option.$isLabel) return option.$groupLabel;

  return option.fullname;
}

function cargarUsuariosById(usuarioIds) {
  if (!usuarioIds) {
    return;
  }
  usuarios.list({ params: { id: usuarioIds } }).then((result) => {
    const lista = prepararUsuarios(result.data);
    if (props.multiple) {
      localValue.value = lista;
    } else {
      localValue.value = lista.length > 0 ? lista[0] : null;
    }
  });
}

function addUsuarios(users) {
  localValue.value = localValue.value || [];
  localValue.value = prepararUsuarios(localValue.value.concat(users));
}

// Los valores que devuelve el multiselect son objetos de tipo usuario, aquí
// mapeamos a enteros
watch(localValue, (value) => {
  let nuevo = value;
  if (Array.isArray(nuevo)) {
    nuevo = value.map((val) => val.id);
  } else if (value instanceof Object) {
    nuevo = nuevo.id;
  }

  // Cambiamos nosotros el valor, hacemos que el watch de value
  // lo omita la gestión
  omitirSiguiente.value = true;
  emit('update:modelValue', nuevo);
});

// Los valores de este componente son integer, si cambia desde fuera
// o hay valor inicial, cargamos el objeto del servidor
watch(() => props.modelValue, (value) => {
  if (omitirSiguiente.value) {
    omitirSiguiente.value = false;
    return;
  }

  cargarUsuariosById(value);
});

onMounted(() => {
  omitirSiguiente.value = false;
  if (props.precargar) {
    ajaxSearch(null);
  } else {
    cargarUsuariosById(props.modelValue);
  }
});

defineExpose({
  addUsuarios,
  usuarioActual,
});
</script>

<template>
  <select-cifra
    v-model="localValue"
    :options="usuariosLista"
    :loading="loading"
    :placeholder="placeholderFinal"
    :multiple="multiple"
    :show-labels="showLabels"
    label="fullname"
    track-by="id"
    @search-change="onSearch"
  >
    <template #option="props">
      <span
        v-if="icon"
        :class="props.option.icon.icono"
        data-toggle="tooltip"
        data-placement="left"
        :title="props.option.icon.title"
      ></span>
      {{ getOptionLabel(props.option) }}
      <span
        v-if="props.option.contador"
        class="label label-info pull-right"
        data-toggle="tooltip"
        :title="props.option.tooltipContador"
      >
        {{ props.option.contador }}
      </span>
    </template>
  </select-cifra>
</template>

<style scoped>
.multiselect__option .fa {
  margin-right: 5px;
}
</style>

